import type {
  BillingStatus,
  BillingType,
  UserBilling,
} from '@/common/db-typebox';
import { useTreatyClient } from '@/lib/client';
import { Card, Select, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';

const { Title } = Typography;

// Map billing types to display names and colors
const billingTypeConfig: Record<BillingType, { label: string; color: string }> =
  {
    subscription: { label: 'Subscription', color: 'blue' },
    one_time: { label: 'One Time', color: 'green' },
  };

// Map billing status to display names and colors
const billingStatusConfig: Record<
  BillingStatus,
  { label: string; color: string }
> = {
  // Subscription statuses
  active: { label: 'Active', color: 'success' },
  canceled: { label: 'Canceled', color: 'default' },
  incomplete: { label: 'Incomplete', color: 'warning' },
  incomplete_expired: { label: 'Expired', color: 'error' },
  past_due: { label: 'Past Due', color: 'error' },
  paused: { label: 'Paused', color: 'warning' },
  trialing: { label: 'Trial', color: 'processing' },
  unpaid: { label: 'Unpaid', color: 'error' },
  // Payment intent statuses
  processing: { label: 'Processing', color: 'processing' },
  requires_action: { label: 'Action Required', color: 'warning' },
  requires_capture: { label: 'Capture Required', color: 'warning' },
  requires_confirmation: { label: 'Confirmation Required', color: 'warning' },
  requires_payment_method: { label: 'Payment Required', color: 'warning' },
  succeeded: { label: 'Succeeded', color: 'success' },
};

export const BillingPage = () => {
  const [loading, setLoading] = useState(false);
  const [billingRecords, setBillingRecords] = useState<UserBilling[]>([]);
  const [statusFilter, setStatusFilter] = useState<BillingStatus[]>([]);
  const client = useTreatyClient();

  useEffect(() => {
    const fetchBilling = async () => {
      try {
        setLoading(true);
        const response = await client.api.user_billing.me.get();
        if (response.data?.success) {
          setBillingRecords(response.data.data.user_billings);
        }
      } catch (error) {
        console.error('Failed to fetch billing info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBilling();
  }, [client]);

  const columns: ColumnsType<UserBilling> = [
    {
      title: 'Type',
      dataIndex: 'billing_type',
      key: 'billing_type',
      width: 120,
      render: (type: BillingType) => (
        <Tag color={billingTypeConfig[type].color}>
          {billingTypeConfig[type].label}
        </Tag>
      ),
      filters: Object.entries(billingTypeConfig).map(([value, { label }]) => ({
        text: label,
        value,
      })),
      onFilter: (value, record) => record.billing_type === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: BillingStatus) => (
        <Tag color={billingStatusConfig[status].color}>
          {billingStatusConfig[status].label}
        </Tag>
      ),
      filters: Object.entries(billingStatusConfig).map(
        ([value, { label }]) => ({
          text: label,
          value,
        }),
      ),
      onFilter: (value, record) => record.status === value,
      filteredValue: statusFilter,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (amount: number, record: UserBilling) =>
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: record.currency,
        }).format(amount),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: 'Period End',
      dataIndex: 'period_end',
      key: 'period_end',
      width: 150,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString() : 'N/A',
      sorter: (a, b) => {
        if (!a.period_end) return 1;
        if (!b.period_end) return -1;
        return (
          new Date(a.period_end).getTime() - new Date(b.period_end).getTime()
        );
      },
    },
    {
      title: 'Customer ID',
      dataIndex: 'customer_id',
      key: 'customer_id',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Payment ID',
      dataIndex: 'payment_id',
      key: 'payment_id',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
      sorter: (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend',
    },
  ];

  const filteredRecords = billingRecords.filter(
    (record) =>
      statusFilter.length === 0 || statusFilter.includes(record.status),
  );

  return (
    <div className="p-8">
      <div className="space-y-6">
        <Card>
          <div className="flex justify-between items-center mb-4">
            <Title level={2}>Billing History</Title>
            <Select
              mode="multiple"
              placeholder="Filter by status"
              style={{ width: 300 }}
              onChange={(values) => setStatusFilter(values as BillingStatus[])}
              options={Object.entries(billingStatusConfig).map(
                ([value, { label }]) => ({
                  label,
                  value,
                }),
              )}
              allowClear
            />
          </div>
          <Table
            columns={columns}
            dataSource={filteredRecords}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} records`,
            }}
            scroll={{ x: 1200 }}
          />
        </Card>
      </div>
    </div>
  );
};
